import React from 'react';
import { Share2, CheckCircle, Users, Target, TrendingUp } from 'lucide-react';

export default function SocialMedia() {
  const framework = [
    {
      phase: 'Phase 1: Audit & Alignment',
      duration: 'Weeks 1-2',
      activities: [
        'Channel performance audit across paid, organic, and community metrics',
        'Brand voice, creative system, and governance assessment',
        'Audience segmentation and content pillar refresh',
        'North star KPI alignment with leadership and revenue teams'
      ]
    },
    {
      phase: 'Phase 2: Content System Build',
      duration: 'Weeks 3-5',
      activities: [
        'Modular content calendar that aligns merchandising with storytelling',
        'Production workflows for studio, UGC, and livestream formats',
        'Asset toolkit covering templates, motion, and caption formulas',
        'Social listening set-up to capture signals for product and CX teams'
      ]
    },
    {
      phase: 'Phase 3: Growth Activation',
      duration: 'Weeks 6-10',
      activities: [
        'Always-on testing framework for hooks, offers, and creative formats',
        'Paid and organic amplification laddering with influencer and community programming',
        'Lifecycle retargeting that bridges social, email, and SMS',
        'Community management rituals to convert followers into advocates'
      ]
    },
    {
      phase: 'Phase 4: Optimize & Scale',
      duration: 'Ongoing',
      activities: [
        'Weekly performance stand-ups with insight-to-action loops',
        'Quarterly playbook refresh tied to platform algorithm shifts',
        'Social commerce integrations for live shopping and shoppable video',
        'Executive dashboards linking social investment to pipeline and revenue'
      ]
    }
  ];

  const differentiators = [
    {
      title: 'Full-Funnel Content Engine',
      icon: Share2,
      description: 'From thumb-stopping awareness to conversion-ready retargeting, we orchestrate a feed that supports the whole funnel.'
    },
    {
      title: 'Community-First Mindset',
      icon: Users,
      description: 'Response SLAs, surprise-and-delight playbooks, and ambassador programs turn your comments section into a retention channel.'
    },
    {
      title: 'Revenue Attribution',
      icon: Target,
      description: 'UTM discipline, pixel governance, and post-purchase surveys tie social spend to dollars in the bank.'
    },
    {
      title: 'Creative Iteration Sprints',
      icon: TrendingUp,
      description: 'Rapid experimentation surfaces winning hooks and formats, then feeds them back into paid and lifecycle teams.'
    }
  ];

  const deliverables = [
    '90-day social roadmap with key campaigns, experiments, and KPIs',
    'Content calendar templates and asset production workflows',
    'Community management handbook including tone, response ladders, and escalation paths',
    'Attribution and analytics dashboard schema connecting social to sales'
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center">
              <Share2 size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-5xl font-bold text-[#000000]">Social Media Marketing</h1>
              <p className="text-xl text-gray-600 mt-2">Build communities, convert demand, and keep the conversation going</p>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">The Connected Social Framework</h2>
          <p className="text-lg text-gray-700 mb-8">
            Social should be more than a highlight reel. We combine storytelling, data, and operations so every post, story, and live moment ladders into growth.
          </p>
          <div className="space-y-6">
            {framework.map((phase, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xl">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#000000] mb-2">{phase.phase}</h3>
                    <p className="text-[#38A3A5] font-semibold mb-4">Timeline: {phase.duration}</p>
                  </div>
                </div>
                <ul className="space-y-3 ml-16">
                  {phase.activities.map((activity, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-[#38A3A5] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{activity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">Why Brands Partner With Us</h2>
          <p className="text-lg text-gray-700 mb-8">
            We blend creative ambition with operational rigor, making social a dependable growth and retention channel—not an afterthought.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {differentiators.map((item, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#000000]">{item.title}</h3>
                </div>
                <p className="text-gray-700">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">Deliverables</h2>
          <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8">
            <ul className="space-y-3">
              {deliverables.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-[#38A3A5] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Let’s Build a Social Presence That Sells</h2>
          <p className="text-xl mb-8 opacity-90">
            From strategy to execution, we’ll run the programs that keep your brand top of feed and top of mind.
          </p>
          <a
            href="mailto:contact@motifunbound.com?subject=Request%20a%20Social%20Strategy%20Session&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI'd%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
            className="inline-block bg-white text-[#38A3A5] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all"
          >
            Request a Social Strategy Session
          </a>
        </div>
      </div>
    </div>
  );
}
